// ============================================================================
// EA-KVM Low-Level On-Screen Virtual Keyboard (OSD) & System Macro Matrix
// Supports live AZERTY FR / QWERTY US layout switching, sticky modifiers
// (Ctrl, Shift, Alt, Super, AltGr), Function row (F1-F12), Navigation cluster,
// and direct X11 keysym injection into the NXKV binary stream.
// ============================================================================

import { iconSvg } from '../components/Icons';

export interface OsdKeyDef {
  label: string;
  shiftLabel?: string;
  keysym: number;
  shiftKeysym?: number;
  width?: number;
  isModifier?: 'CTRL' | 'SHIFT' | 'ALT' | 'SUPER' | 'ALTGR' | 'CAPS';
}

export interface OnScreenKeyboardCallbacks {
  onKeysym: (keysym: number, pressed: boolean, label: string) => void;
  onMacro: (macro: string) => void;
}

const FUNCTION_ROW: OsdKeyDef[] = [
  { label: 'ESC', keysym: 0xff1b, width: 1.2 },
  { label: 'F1', keysym: 0xffbe },
  { label: 'F2', keysym: 0xffbf },
  { label: 'F3', keysym: 0xffc0 },
  { label: 'F4', keysym: 0xffc1 },
  { label: 'F5', keysym: 0xffc2 },
  { label: 'F6', keysym: 0xffc3 },
  { label: 'F7', keysym: 0xffc4 },
  { label: 'F8', keysym: 0xffc5 },
  { label: 'F9', keysym: 0xffc6 },
  { label: 'F10', keysym: 0xffc7 },
  { label: 'F11', keysym: 0xffc8 },
  { label: 'F12', keysym: 0xffc9 },
  { label: 'INS', keysym: 0xff63 },
  { label: 'DEL', keysym: 0xffff },
  { label: 'HOME', keysym: 0xff50 },
  { label: 'END', keysym: 0xff57 },
  { label: 'PGUP', keysym: 0xff55 },
  { label: 'PGDN', keysym: 0xff56 }
];

const QWERTY_ROWS: OsdKeyDef[][] = [
  [
    { label: '`', shiftLabel: '~', keysym: 0x0060, shiftKeysym: 0x007e },
    { label: '1', shiftLabel: '!', keysym: 0x0031, shiftKeysym: 0x0021 },
    { label: '2', shiftLabel: '@', keysym: 0x0032, shiftKeysym: 0x0040 },
    { label: '3', shiftLabel: '#', keysym: 0x0033, shiftKeysym: 0x0023 },
    { label: '4', shiftLabel: '$', keysym: 0x0034, shiftKeysym: 0x0024 },
    { label: '5', shiftLabel: '%', keysym: 0x0035, shiftKeysym: 0x0025 },
    { label: '6', shiftLabel: '^', keysym: 0x0036, shiftKeysym: 0x005e },
    { label: '7', shiftLabel: '&', keysym: 0x0037, shiftKeysym: 0x0026 },
    { label: '8', shiftLabel: '*', keysym: 0x0038, shiftKeysym: 0x002a },
    { label: '9', shiftLabel: '(', keysym: 0x0039, shiftKeysym: 0x0028 },
    { label: '0', shiftLabel: ')', keysym: 0x0030, shiftKeysym: 0x0029 },
    { label: '-', shiftLabel: '_', keysym: 0x002d, shiftKeysym: 0x005f },
    { label: '=', shiftLabel: '+', keysym: 0x003d, shiftKeysym: 0x002b },
    { label: 'BKSP', keysym: 0xff08, width: 1.8 }
  ],
  [
    { label: 'TAB', keysym: 0xff09, width: 1.4 },
    { label: 'q', shiftLabel: 'Q', keysym: 0x0071, shiftKeysym: 0x0051 },
    { label: 'w', shiftLabel: 'W', keysym: 0x0077, shiftKeysym: 0x0057 },
    { label: 'e', shiftLabel: 'E', keysym: 0x0065, shiftKeysym: 0x0045 },
    { label: 'r', shiftLabel: 'R', keysym: 0x0072, shiftKeysym: 0x0052 },
    { label: 't', shiftLabel: 'T', keysym: 0x0074, shiftKeysym: 0x0054 },
    { label: 'y', shiftLabel: 'Y', keysym: 0x0079, shiftKeysym: 0x0059 },
    { label: 'u', shiftLabel: 'U', keysym: 0x0075, shiftKeysym: 0x0055 },
    { label: 'i', shiftLabel: 'I', keysym: 0x0069, shiftKeysym: 0x0049 },
    { label: 'o', shiftLabel: 'O', keysym: 0x006f, shiftKeysym: 0x004f },
    { label: 'p', shiftLabel: 'P', keysym: 0x0070, shiftKeysym: 0x0050 },
    { label: '[', shiftLabel: '{', keysym: 0x005b, shiftKeysym: 0x007b },
    { label: ']', shiftLabel: '}', keysym: 0x005d, shiftKeysym: 0x007d },
    { label: '\\', shiftLabel: '|', keysym: 0x005c, shiftKeysym: 0x007c, width: 1.4 }
  ],
  [
    { label: 'CAPS', keysym: 0xffe5, width: 1.6, isModifier: 'CAPS' },
    { label: 'a', shiftLabel: 'A', keysym: 0x0061, shiftKeysym: 0x0041 },
    { label: 's', shiftLabel: 'S', keysym: 0x0073, shiftKeysym: 0x0053 },
    { label: 'd', shiftLabel: 'D', keysym: 0x0064, shiftKeysym: 0x0044 },
    { label: 'f', shiftLabel: 'F', keysym: 0x0066, shiftKeysym: 0x0046 },
    { label: 'g', shiftLabel: 'G', keysym: 0x0067, shiftKeysym: 0x0047 },
    { label: 'h', shiftLabel: 'H', keysym: 0x0068, shiftKeysym: 0x0048 },
    { label: 'j', shiftLabel: 'J', keysym: 0x006a, shiftKeysym: 0x004a },
    { label: 'k', shiftLabel: 'K', keysym: 0x006b, shiftKeysym: 0x004b },
    { label: 'l', shiftLabel: 'L', keysym: 0x006c, shiftKeysym: 0x004c },
    { label: ';', shiftLabel: ':', keysym: 0x003b, shiftKeysym: 0x003a },
    { label: "'", shiftLabel: '"', keysym: 0x0027, shiftKeysym: 0x0022 },
    { label: 'ENTER', keysym: 0xff0d, width: 2.0 }
  ],
  [
    { label: 'SHIFT', keysym: 0xffe1, width: 2.1, isModifier: 'SHIFT' },
    { label: 'z', shiftLabel: 'Z', keysym: 0x007a, shiftKeysym: 0x005a },
    { label: 'x', shiftLabel: 'X', keysym: 0x0078, shiftKeysym: 0x0058 },
    { label: 'c', shiftLabel: 'C', keysym: 0x0063, shiftKeysym: 0x0043 },
    { label: 'v', shiftLabel: 'V', keysym: 0x0076, shiftKeysym: 0x0056 },
    { label: 'b', shiftLabel: 'B', keysym: 0x0062, shiftKeysym: 0x0042 },
    { label: 'n', shiftLabel: 'N', keysym: 0x006e, shiftKeysym: 0x004e },
    { label: 'm', shiftLabel: 'M', keysym: 0x006d, shiftKeysym: 0x004d },
    { label: ',', shiftLabel: '<', keysym: 0x002c, shiftKeysym: 0x003c },
    { label: '.', shiftLabel: '>', keysym: 0x002e, shiftKeysym: 0x003e },
    { label: '/', shiftLabel: '?', keysym: 0x002f, shiftKeysym: 0x003f },
    { label: 'UP', keysym: 0xff52, width: 1.2 },
    { label: 'SHIFT', keysym: 0xffe2, width: 1.5, isModifier: 'SHIFT' }
  ]
];

const AZERTY_ROWS: OsdKeyDef[][] = [
  [
    { label: '²', shiftLabel: '~', keysym: 0x00b2, shiftKeysym: 0x007e },
    { label: '&', shiftLabel: '1', keysym: 0x0026, shiftKeysym: 0x0031 },
    { label: 'é', shiftLabel: '2', keysym: 0x00e9, shiftKeysym: 0x0032 },
    { label: '"', shiftLabel: '3', keysym: 0x0022, shiftKeysym: 0x0033 },
    { label: "'", shiftLabel: '4', keysym: 0x0027, shiftKeysym: 0x0034 },
    { label: '(', shiftLabel: '5', keysym: 0x0028, shiftKeysym: 0x0035 },
    { label: '-', shiftLabel: '6', keysym: 0x002d, shiftKeysym: 0x0036 },
    { label: 'è', shiftLabel: '7', keysym: 0x00e8, shiftKeysym: 0x0037 },
    { label: '_', shiftLabel: '8', keysym: 0x005f, shiftKeysym: 0x0038 },
    { label: 'ç', shiftLabel: '9', keysym: 0x00e7, shiftKeysym: 0x0039 },
    { label: 'à', shiftLabel: '0', keysym: 0x00e0, shiftKeysym: 0x0030 },
    { label: ')', shiftLabel: '°', keysym: 0x0029, shiftKeysym: 0x00b0 },
    { label: '=', shiftLabel: '+', keysym: 0x003d, shiftKeysym: 0x002b },
    { label: 'BKSP', keysym: 0xff08, width: 1.8 }
  ],
  [
    { label: 'TAB', keysym: 0xff09, width: 1.4 },
    { label: 'a', shiftLabel: 'A', keysym: 0x0061, shiftKeysym: 0x0041 },
    { label: 'z', shiftLabel: 'Z', keysym: 0x007a, shiftKeysym: 0x005a },
    { label: 'e', shiftLabel: 'E', keysym: 0x0065, shiftKeysym: 0x0045 },
    { label: 'r', shiftLabel: 'R', keysym: 0x0072, shiftKeysym: 0x0052 },
    { label: 't', shiftLabel: 'T', keysym: 0x0074, shiftKeysym: 0x0054 },
    { label: 'y', shiftLabel: 'Y', keysym: 0x0079, shiftKeysym: 0x0059 },
    { label: 'u', shiftLabel: 'U', keysym: 0x0075, shiftKeysym: 0x0055 },
    { label: 'i', shiftLabel: 'I', keysym: 0x0069, shiftKeysym: 0x0049 },
    { label: 'o', shiftLabel: 'O', keysym: 0x006f, shiftKeysym: 0x004f },
    { label: 'p', shiftLabel: 'P', keysym: 0x0070, shiftKeysym: 0x0050 },
    { label: '^', shiftLabel: '¨', keysym: 0x005e, shiftKeysym: 0x00a8 },
    { label: '$', shiftLabel: '£', keysym: 0x0024, shiftKeysym: 0x00a3 },
    { label: '*', shiftLabel: 'µ', keysym: 0x002a, shiftKeysym: 0x00b5, width: 1.4 }
  ],
  [
    { label: 'CAPS', keysym: 0xffe5, width: 1.6, isModifier: 'CAPS' },
    { label: 'q', shiftLabel: 'Q', keysym: 0x0071, shiftKeysym: 0x0051 },
    { label: 's', shiftLabel: 'S', keysym: 0x0073, shiftKeysym: 0x0053 },
    { label: 'd', shiftLabel: 'D', keysym: 0x0064, shiftKeysym: 0x0044 },
    { label: 'f', shiftLabel: 'F', keysym: 0x0066, shiftKeysym: 0x0046 },
    { label: 'g', shiftLabel: 'G', keysym: 0x0067, shiftKeysym: 0x0047 },
    { label: 'h', shiftLabel: 'H', keysym: 0x0068, shiftKeysym: 0x0048 },
    { label: 'j', shiftLabel: 'J', keysym: 0x006a, shiftKeysym: 0x004a },
    { label: 'k', shiftLabel: 'K', keysym: 0x006b, shiftKeysym: 0x004b },
    { label: 'l', shiftLabel: 'L', keysym: 0x006c, shiftKeysym: 0x004c },
    { label: 'm', shiftLabel: 'M', keysym: 0x006d, shiftKeysym: 0x004d },
    { label: 'ù', shiftLabel: '%', keysym: 0x00f9, shiftKeysym: 0x0025 },
    { label: 'ENTER', keysym: 0xff0d, width: 2.0 }
  ],
  [
    { label: 'SHIFT', keysym: 0xffe1, width: 2.1, isModifier: 'SHIFT' },
    { label: 'w', shiftLabel: 'W', keysym: 0x0077, shiftKeysym: 0x0057 },
    { label: 'x', shiftLabel: 'X', keysym: 0x0078, shiftKeysym: 0x0058 },
    { label: 'c', shiftLabel: 'C', keysym: 0x0063, shiftKeysym: 0x0043 },
    { label: 'v', shiftLabel: 'V', keysym: 0x0076, shiftKeysym: 0x0056 },
    { label: 'b', shiftLabel: 'B', keysym: 0x0062, shiftKeysym: 0x0042 },
    { label: 'n', shiftLabel: 'N', keysym: 0x006e, shiftKeysym: 0x004e },
    { label: ',', shiftLabel: '?', keysym: 0x002c, shiftKeysym: 0x003f },
    { label: ';', shiftLabel: '.', keysym: 0x003b, shiftKeysym: 0x002e },
    { label: ':', shiftLabel: '/', keysym: 0x003a, shiftKeysym: 0x002f },
    { label: '!', shiftLabel: '§', keysym: 0x0021, shiftKeysym: 0x00a7 },
    { label: 'UP', keysym: 0xff52, width: 1.2 },
    { label: 'SHIFT', keysym: 0xffe2, width: 1.5, isModifier: 'SHIFT' }
  ]
];

const BOTTOM_ROW: OsdKeyDef[] = [
  { label: 'CTRL', keysym: 0xffe3, width: 1.4, isModifier: 'CTRL' },
  { label: 'SUPER', keysym: 0xffeb, width: 1.3, isModifier: 'SUPER' },
  { label: 'ALT', keysym: 0xffe9, width: 1.3, isModifier: 'ALT' },
  { label: 'SPACE', keysym: 0x0020, width: 5.8 },
  { label: 'ALTGR', keysym: 0xffea, width: 1.4, isModifier: 'ALTGR' },
  { label: 'CTRL', keysym: 0xffe4, width: 1.4, isModifier: 'CTRL' },
  { label: 'LEFT', keysym: 0xff51, width: 1.1 },
  { label: 'DOWN', keysym: 0xff54, width: 1.1 },
  { label: 'RIGHT', keysym: 0xff53, width: 1.1 }
];

export class OnScreenKeyboard {
  private readonly callbacks: OnScreenKeyboardCallbacks;
  private visible = false;
  private layout: 'AZERTY_FR' | 'QWERTY_US' = 'AZERTY_FR';
  private stickyMods: Set<'CTRL' | 'SHIFT' | 'ALT' | 'SUPER' | 'ALTGR' | 'CAPS'> = new Set();

  constructor(callbacks: OnScreenKeyboardCallbacks) {
    this.callbacks = callbacks;
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public setVisible(visible: boolean): void {
    this.visible = visible;
  }

  public toggleVisible(): boolean {
    this.visible = !this.visible;
    return this.visible;
  }

  public setLayout(layout: 'AZERTY_FR' | 'QWERTY_US'): void {
    this.layout = layout;
  }

  public renderHtml(): string {
    if (!this.visible) return '';
    const rows = this.layout === 'AZERTY_FR' ? AZERTY_ROWS : QWERTY_ROWS;
    const shiftActive = this.stickyMods.has('SHIFT') || this.stickyMods.has('CAPS');

    const renderKey = (k: OsdKeyDef, rIdx: number, kIdx: number): string => {
      const isModActive = k.isModifier ? this.stickyMods.has(k.isModifier) : false;
      const displayLabel = shiftActive && k.shiftLabel ? k.shiftLabel : k.label;
      const flexBasis = (k.width || 1) * 38;
      return `
        <button
          type="button"
          class="nx-osd-key nx-full-osd-key ${isModActive ? 'nx-btn-primary' : ''}"
          style="flex:${k.width || 1} 1 ${flexBasis}px;height:28px;font-size:11px;display:flex;align-items:center;justify-content:center;border-color:${isModActive ? '#3B82F6' : '#1E2430'};"
          data-row="${rIdx}"
          data-col="${kIdx}"
          data-fn="${rIdx === -1 ? '1' : rIdx === 99 ? '2' : '0'}"
        >
          ${this.escapeHtml(displayLabel)}
        </button>
      `;
    };

    return `
      <div id="nx-full-osd-overlay" style="position:absolute;bottom:10px;left:50%;transform:translateX(-50%);width:min(860px, 96%);background:rgba(16,19,24,0.96);border:1px solid #2A3242;border-radius:4px;padding:8px 10px;z-index:35;box-shadow:0 8px 24px rgba(0,0,0,0.65);">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid #1E2430;">
          <div style="display:flex;align-items:center;gap:8px;font-family:var(--font-mono);font-size:11px;">
            ${iconSvg('keyboard', 13)}
            <span style="font-weight:600;color:#F3F4F6;">ON-SCREEN HID VIRTUAL KEYBOARD (OSD)</span>
            <button id="nx-osd-layout-azerty" class="nx-btn ${this.layout === 'AZERTY_FR' ? 'nx-btn-primary' : ''}" style="height:22px;padding:0 6px;font-size:10px;" type="button">AZERTY FR</button>
            <button id="nx-osd-layout-qwerty" class="nx-btn ${this.layout === 'QWERTY_US' ? 'nx-btn-primary' : ''}" style="height:22px;padding:0 6px;font-size:10px;" type="button">QWERTY US</button>
            <span style="color:#9CA3AF;font-size:10px;">Sticky Modifiers: ${Array.from(this.stickyMods).join(' + ') || 'NONE'}</span>
          </div>
          <div style="display:flex;align-items:center;gap:4px;">
            <button class="nx-btn nx-osd-quick-macro" data-macro="CTRL_ALT_DEL" style="height:22px;padding:0 6px;font-size:10px;" type="button">Ctrl+Alt+Del</button>
            <button class="nx-btn nx-osd-quick-macro" data-macro="CTRL_ALT_F1" style="height:22px;padding:0 6px;font-size:10px;" type="button">TTY1</button>
            <button class="nx-btn nx-osd-quick-macro" data-macro="CTRL_ALT_F3" style="height:22px;padding:0 6px;font-size:10px;" type="button">TTY3</button>
            <button class="nx-btn nx-osd-quick-macro" data-macro="ALT_SYSRQ_REISUB" style="height:22px;padding:0 6px;font-size:10px;" type="button">REISUB</button>
            <button id="nx-osd-close" class="nx-btn" style="height:22px;padding:0 6px;font-size:10px;" type="button">${iconSvg('x', 11)}</button>
          </div>
        </div>

        <div style="display:flex;flex-direction:column;gap:4px;">
          <div style="display:flex;gap:3px;">
            ${FUNCTION_ROW.map((k, idx) => renderKey(k, -1, idx)).join('')}
          </div>
          ${rows.map((row, rIdx) => `
            <div style="display:flex;gap:3px;">
              ${row.map((k, kIdx) => renderKey(k, rIdx, kIdx)).join('')}
            </div>
          `).join('')}
          <div style="display:flex;gap:3px;">
            ${BOTTOM_ROW.map((k, idx) => renderKey(k, 99, idx)).join('')}
          </div>
        </div>
      </div>
    `;
  }

  public bindEvents(container: HTMLElement, reRender: () => void): void {
    if (!this.visible) return;

    container.querySelector('#nx-osd-layout-azerty')?.addEventListener('click', () => {
      this.layout = 'AZERTY_FR';
      reRender();
    });

    container.querySelector('#nx-osd-layout-qwerty')?.addEventListener('click', () => {
      this.layout = 'QWERTY_US';
      reRender();
    });

    container.querySelector('#nx-osd-close')?.addEventListener('click', () => {
      this.visible = false;
      reRender();
    });

    container.querySelectorAll('.nx-osd-quick-macro').forEach(btn => {
      btn.addEventListener('click', () => {
        const macro = (btn as HTMLElement).dataset.macro || 'CTRL_ALT_DEL';
        this.callbacks.onMacro(macro);
      });
    });

    const rows = this.layout === 'AZERTY_FR' ? AZERTY_ROWS : QWERTY_ROWS;

    container.querySelectorAll('.nx-full-osd-key').forEach(btn => {
      btn.addEventListener('click', () => {
        const el = btn as HTMLElement;
        const rIdx = Number(el.dataset.row || 0);
        const kIdx = Number(el.dataset.col || 0);
        const fnMode = el.dataset.fn || '0';

        let keyDef: OsdKeyDef | undefined;
        if (fnMode === '1') {
          keyDef = FUNCTION_ROW[kIdx];
        } else if (fnMode === '2') {
          keyDef = BOTTOM_ROW[kIdx];
        } else {
          keyDef = rows[rIdx]?.[kIdx];
        }
        if (!keyDef) return;

        if (keyDef.isModifier) {
          const mod = keyDef.isModifier;
          if (this.stickyMods.has(mod)) {
            this.stickyMods.delete(mod);
            this.callbacks.onKeysym(keyDef.keysym, false, keyDef.label);
          } else {
            this.stickyMods.add(mod);
            this.callbacks.onKeysym(keyDef.keysym, true, keyDef.label);
          }
          reRender();
          return;
        }

        const shiftActive = this.stickyMods.has('SHIFT') || this.stickyMods.has('CAPS');
        const activeKeysym = shiftActive && keyDef.shiftKeysym ? keyDef.shiftKeysym : keyDef.keysym;
        const activeLabel = shiftActive && keyDef.shiftLabel ? keyDef.shiftLabel : keyDef.label;

        this.callbacks.onKeysym(activeKeysym, true, activeLabel);
        this.callbacks.onKeysym(activeKeysym, false, activeLabel);

        // Release non-CAPS sticky modifiers after one keystroke
        const transientMods: Array<'CTRL' | 'SHIFT' | 'ALT' | 'SUPER' | 'ALTGR'> = ['CTRL', 'SHIFT', 'ALT', 'SUPER', 'ALTGR'];
        const modKeysyms: Record<string, number> = {
          CTRL: 0xffe3,
          SHIFT: 0xffe1,
          ALT: 0xffe9,
          SUPER: 0xffeb,
          ALTGR: 0xffea
        };
        let releasedAny = false;
        for (const tm of transientMods) {
          if (this.stickyMods.has(tm)) {
            this.stickyMods.delete(tm);
            this.callbacks.onKeysym(modKeysyms[tm], false, tm);
            releasedAny = true;
          }
        }
        if (releasedAny) {
          reRender();
        }
      });
    });
  }

  private escapeHtml(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
